import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  addDoc,
  collection,
  collectionGroup,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import moment from "moment";
import { DATABASE, STORAGE } from "../../../Config/firebase";
import AddWalletModal from "../WalletManagement/components/AddWalletModal";
import {
  CRYPTO_ASSETS,
  CRYPTO_NETWORKS,
  DEFAULT_ASSET,
  DEFAULT_NETWORK,
  maskAddress,
  validateWalletAddress,
} from "../../../Utils/cryptoWalletUtils";
import {
  decodeQrFromFile,
  detectNetworkFromQrText,
  looksLikePaymentUri,
  normalizeAddressForCompare,
  parseWalletFromQr,
} from "../../../Utils/walletQr";
import QrScanner from "./components/QrScanner";

const PANEL_SX = {
  p: 2,
  borderRadius: 2,
  bgcolor: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
};

const FIELD_INPUT_SX = { color: "#fff" };
const FIELD_LABEL_SX = { color: "rgba(255,255,255,0.6)" };

const MATCH_CONFIG = {
  match: {
    label: "Matches a registered wallet",
    short: "Match",
    hint: "This address is already registered for this client.",
    color: "#2af598",
    bg: "rgba(42,245,152,0.12)",
    border: "rgba(42,245,152,0.45)",
    Icon: CheckCircleIcon,
  },
  no_match: {
    label: "New wallet — not in the registered list",
    short: "New wallet",
    hint: "Different from the wallets on file. Save it here for review; an admin can add it to Wallet Management later, after a manual check.",
    color: "#7eb8ff",
    bg: "rgba(126,184,255,0.12)",
    border: "rgba(126,184,255,0.45)",
    Icon: BookmarkAddIcon,
  },
  not_registered: {
    label: "New wallet — client has none registered yet",
    short: "New wallet",
    hint: "This client has no registered wallet yet. Save it here for review; an admin can add it to Wallet Management later, after a manual check.",
    color: "#7eb8ff",
    bg: "rgba(126,184,255,0.12)",
    border: "rgba(126,184,255,0.45)",
    Icon: BookmarkAddIcon,
  },
};

const formatTs = (ts) => {
  if (!ts) return "—";
  const m = ts?.toDate ? moment(ts.toDate()) : ts?.seconds ? moment(ts.seconds * 1000) : null;
  return m?.isValid() ? m.format("DD MMM YYYY, HH:mm") : "—";
};

const WalletCheck = () => {
  const [searchParams] = useSearchParams();
  const clientFromUrl = searchParams.get("client") || "";

  // Clients
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  // Official wallets (read-only)
  const [officialWallets, setOfficialWallets] = useState([]);
  const [inactiveWallets, setInactiveWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [revealed, setRevealed] = useState({});

  // Verification input
  const [address, setAddress] = useState("");
  const [network, setNetwork] = useState(DEFAULT_NETWORK);
  const [asset, setAsset] = useState(DEFAULT_ASSET);
  const [qrRaw, setQrRaw] = useState("");
  const [notes, setNotes] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [decodingPhoto, setDecodingPhoto] = useState(false);

  // Save + history
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Import to client profile (opens Wallet Management add form, prefilled)
  const [importOpen, setImportOpen] = useState(false);
  const [importPrefill, setImportPrefill] = useState(null);

  // Team progress (global, across all clients)
  const [allConfirmations, setAllConfirmations] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);

  const fileInputRef = useRef(null);

  useEffect(() => {
    setLoadingClients(true);
    getDocs(query(collection(DATABASE, "users"), where("role", "==", 1)))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, doc_id: d.id, ...d.data() }))
          .sort((a, b) =>
            (a.full_name || a.email || "").localeCompare(
              b.full_name || b.email || "",
              undefined,
              { sensitivity: "base" }
            )
          );
        setClients(list);
        if (clientFromUrl) {
          const match = list.find((c) => (c.doc_id || c.id) === clientFromUrl);
          if (match) setSelectedClient(match);
        }
      })
      .catch(() => toast.error("Failed to load clients"))
      .finally(() => setLoadingClients(false));
  }, [clientFromUrl]);

  const loadOfficialWallets = useCallback((userId) => {
    if (!userId) return;
    setLoadingWallets(true);
    getDocs(collection(DATABASE, "users", userId, "crypto_wallets"))
      .then((snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const active = all
          .filter((w) => w.is_active !== false)
          .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
        const inactive = all
          .filter((w) => w.is_active === false)
          .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
        setOfficialWallets(active);
        setInactiveWallets(inactive);
      })
      .catch(() => {
        toast.error("Failed to load registered wallets");
        setOfficialWallets([]);
        setInactiveWallets([]);
      })
      .finally(() => setLoadingWallets(false));
  }, []);

  const loadProgress = useCallback(() => {
    setLoadingProgress(true);
    getDocs(collectionGroup(DATABASE, "wallet_confirmations"))
      .then((snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          userId: d.ref.parent.parent?.id || "",
          ...d.data(),
        }));
        rows.sort(
          (a, b) => (b.confirmed_at?.seconds || 0) - (a.confirmed_at?.seconds || 0)
        );
        setAllConfirmations(rows);
      })
      .catch((e) => {
        console.error("Failed to load wallet-check progress", e);
        setAllConfirmations([]);
      })
      .finally(() => setLoadingProgress(false));
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const loadHistory = useCallback((userId) => {
    if (!userId) return;
    setLoadingHistory(true);
    getDocs(
      query(
        collection(DATABASE, "users", userId, "wallet_confirmations"),
        orderBy("confirmed_at", "desc"),
        limit(5)
      )
    )
      .then((snap) => setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, []);

  useEffect(() => {
    const userId = selectedClient?.doc_id || selectedClient?.id;
    if (!userId) {
      setOfficialWallets([]);
      setInactiveWallets([]);
      setHistory([]);
      return;
    }
    loadOfficialWallets(userId);
    loadHistory(userId);
  }, [selectedClient, loadOfficialWallets, loadHistory]);

  const clientNameById = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      map[c.doc_id || c.id] = c.full_name || c.email || (c.doc_id || c.id);
    });
    return map;
  }, [clients]);

  const progressStats = useMemo(() => {
    const total = allConfirmations.length;
    const newWallets = allConfirmations.filter(
      (c) => c.is_new_wallet || (c.match_status && c.match_status !== "match")
    ).length;
    const clientsTouched = new Set(
      allConfirmations.map((c) => c.userId).filter(Boolean)
    ).size;
    return { total, newWallets, matched: total - newWallets, clientsTouched };
  }, [allConfirmations]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return clients
      .filter(
        (c) =>
          (c.full_name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q)
      )
      .slice(0, 25);
  }, [clients, search]);

  const resetVerification = () => {
    setAddress("");
    setNetwork(DEFAULT_NETWORK);
    setAsset(DEFAULT_ASSET);
    setQrRaw("");
    setNotes("");
    setShowCamera(false);
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(null);
    setProofPreview("");
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearch("");
    resetVerification();
  };

  const applyDecoded = (parsed) => {
    if (parsed?.address) {
      setAddress(parsed.address);
      if (parsed.network) setNetwork(parsed.network);
      setQrRaw(parsed.raw || "");
      if (parsed.valid) toast.success("Wallet address read from QR code");
      else toast.warn("QR read, but the address could not be validated — please check");
    } else {
      toast.warn("No wallet QR code found — type or paste the address");
    }
  };

  const handleCameraDecode = (text) => {
    setShowCamera(false);
    applyDecoded(parseWalletFromQr(text));
  };

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (proofPreview) URL.revokeObjectURL(proofPreview);
    setProofFile(file);
    setProofPreview(URL.createObjectURL(file));
    setDecodingPhoto(true);
    try {
      const parsed = await decodeQrFromFile(file);
      applyDecoded(parsed);
    } catch (err) {
      toast.info("No QR code detected in the photo — type or paste the address");
    } finally {
      setDecodingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onAddressChange = (val) => {
    if (looksLikePaymentUri(val)) {
      const parsed = parseWalletFromQr(val);
      setAddress(parsed.address || val);
      if (parsed.network) setNetwork(parsed.network);
      if (parsed.address && parsed.address !== val) {
        setQrRaw(val.trim());
        toast.info("Payment link parsed — using the recipient wallet address");
      }
      return;
    }
    setAddress(val);
    const detected = detectNetworkFromQrText(val, val.trim());
    if (detected) setNetwork(detected);
  };

  const trimmedAddress = address.trim();
  const addressValidation = useMemo(
    () => (trimmedAddress ? validateWalletAddress(trimmedAddress, network) : { valid: false }),
    [trimmedAddress, network]
  );

  const matchedWallet = useMemo(() => {
    if (!trimmedAddress) return null;
    const incoming = normalizeAddressForCompare(trimmedAddress, network);
    return (
      officialWallets.find(
        (w) =>
          normalizeAddressForCompare(w.address, w.network || DEFAULT_NETWORK) === incoming
      ) || null
    );
  }, [trimmedAddress, network, officialWallets]);

  const matchStatus = useMemo(() => {
    if (!trimmedAddress) return null;
    if (matchedWallet) return "match";
    if (officialWallets.length === 0) return "not_registered";
    return "no_match";
  }, [trimmedAddress, matchedWallet, officialWallets]);

  const toggleReveal = (key) => setRevealed((p) => ({ ...p, [key]: !p[key] }));

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info("Address copied");
  };

  const handleImportClick = async (h) => {
    const clientName = selectedClient?.full_name || selectedClient?.email || "this client";
    const result = await Swal.fire({
      title: "Import wallet to client profile?",
      html:
        `This opens the Add Wallet form pre-filled with this address for <b>${clientName}</b>.<br/><br/>` +
        "The wallet is <b>only added after you review and confirm</b> in the form. A 24h auto-pay cooldown applies after adding.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Continue",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    setImportPrefill({ address: h.address, asset: h.asset, network: h.network, label: "" });
    setImportOpen(true);
  };

  const reloadAfterImport = () => {
    const uid = selectedClient?.doc_id || selectedClient?.id;
    if (uid) {
      loadOfficialWallets(uid);
      loadHistory(uid);
    }
    loadProgress();
  };

  const handleSave = async () => {
    const userId = selectedClient?.doc_id || selectedClient?.id;
    if (!userId) {
      toast.error("Select a client first");
      return;
    }
    if (!trimmedAddress) {
      toast.error("Add the wallet address to confirm");
      return;
    }
    setSaving(true);
    try {
      let photo_url = "";
      let photo_path = "";
      let photoFailed = false;
      if (proofFile) {
        // Upload under Images/ — the only client-writable prefix in Storage rules.
        // Photo is proof, not blocking: if it fails, still save the wallet record.
        try {
          const ext = (proofFile.name?.split(".").pop() || "jpg").toLowerCase();
          photo_path = `Images/wallet_confirmations/${userId}/${Date.now()}.${ext}`;
          const sRef = storageRef(STORAGE, photo_path);
          await uploadBytes(sRef, proofFile);
          photo_url = await getDownloadURL(sRef);
        } catch (photoErr) {
          console.error("Proof photo upload failed", photoErr);
          photoFailed = true;
          photo_url = "";
          photo_path = "";
        }
      }

      const adminEmail = localStorage.getItem("email") || "admin";
      const isNewWallet = matchStatus !== "match";
      await addDoc(collection(DATABASE, "users", userId, "wallet_confirmations"), {
        address: trimmedAddress,
        asset,
        network,
        photo_url,
        photo_path,
        qr_decoded_raw: qrRaw || "",
        match_status: matchStatus || "no_match",
        matched_wallet_id: matchedWallet?.id || matchedWallet?.wallet_id || null,
        is_new_wallet: isNewWallet,
        review_status: isNewWallet ? "pending_review" : "matches_registered",
        notes: notes.trim() || "",
        confirmed_by: adminEmail,
        confirmed_at: serverTimestamp(),
      });

      if (photoFailed) {
        toast.warn(
          isNewWallet
            ? "New wallet saved for review — but the proof photo could not be uploaded"
            : "Confirmation saved — but the proof photo could not be uploaded"
        );
      } else {
        toast.success(isNewWallet ? "New wallet saved for review" : "Confirmation saved");
      }
      resetVerification();
      loadHistory(userId);
      loadProgress();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save confirmation");
    } finally {
      setSaving(false);
    }
  };

  const matchConf = matchStatus ? MATCH_CONFIG[matchStatus] : null;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: "flex", justifyContent: "center" }}>
      <Box sx={{ width: "100%", maxWidth: { xs: 620, md: 1080 } }}>
        {/* ── Header ── */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            Wallet Check
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)", mt: 0.75, lineHeight: 1.5 }}>
            View and confirm a client's withdrawal wallet, or save a brand-new one. Scan/upload the
            QR or photo the client sent, compare with what's registered, and keep the proof. New
            wallets are saved here for review only — an admin adds them to Wallet Management later,
            after a manual check. This never changes the official wallet on its own.
          </Typography>
        </Box>

        {/* ── Client search ── */}
        <Box sx={{ ...PANEL_SX, mb: 2 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Client
          </Typography>
          {selectedClient ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ color: "#fff", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedClient.full_name || selectedClient.email}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  {selectedClient.email}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => { setSelectedClient(null); resetVerification(); }}
                startIcon={<CloseIcon />}
              >
                Change
              </Button>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                size="medium"
                placeholder="Search client by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mt: 1 }}
                InputProps={{
                  sx: { color: "#fff" },
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonSearchIcon sx={{ color: "rgba(255,255,255,0.5)" }} />
                    </InputAdornment>
                  ),
                }}
              />
              {loadingClients && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Loading clients…</Typography>
                </Box>
              )}
              {!loadingClients && search.trim() && filteredClients.length === 0 && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mt: 1.5 }}>
                  No clients found.
                </Typography>
              )}
              {filteredClients.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 300, overflowY: "auto" }}>
                  {filteredClients.map((c) => (
                    <Box
                      key={c.doc_id || c.id}
                      onClick={() => handleSelectClient(c)}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        border: "1px solid rgba(255,255,255,0.1)",
                        bgcolor: "rgba(0,0,0,0.2)",
                        "&:hover": { bgcolor: "rgba(42,245,152,0.08)", borderColor: "rgba(42,245,152,0.35)" },
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                        {c.full_name || c.email}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                        {c.email}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        {selectedClient && (
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 0, md: 2 }, alignItems: "flex-start" }}>
            <Box sx={{ width: "100%", flex: { md: "1 1 0" }, minWidth: 0 }}>
            {/* ── Registered (official) wallet ── */}
            <Box sx={{ ...PANEL_SX, mb: 2 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Registered wallet (official)
              </Typography>
              {loadingWallets ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Loading…</Typography>
                </Box>
              ) : officialWallets.length === 0 ? (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                  No active wallet registered for this client yet.
                </Alert>
              ) : (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {officialWallets.map((w) => {
                    const net = w.network || DEFAULT_NETWORK;
                    const isRevealed = revealed[w.id];
                    return (
                      <Box
                        key={w.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: "1px solid rgba(255,255,255,0.12)",
                          bgcolor: "rgba(0,0,0,0.25)",
                        }}
                      >
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75, alignItems: "center" }}>
                          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 700, mr: 0.5 }}>
                            {w.label || `${w.asset || DEFAULT_ASSET} Wallet`}
                          </Typography>
                          <Chip size="small" label={w.asset || DEFAULT_ASSET} sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "rgba(42,245,152,0.12)", color: "#2af598" }} />
                          <Chip size="small" label={net} variant="outlined" sx={{ height: 20, fontSize: 10, color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.25)" }} />
                          {w.is_default && <Chip size="small" label="Default" color="success" sx={{ height: 20, fontSize: 10 }} />}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography
                            sx={{ fontFamily: "monospace", fontSize: 14, color: "#fff", wordBreak: "break-all", flex: 1 }}
                          >
                            {isRevealed ? w.address : maskAddress(w.address, net)}
                          </Typography>
                          <IconButton size="small" onClick={() => toggleReveal(w.id)} sx={{ color: "rgba(255,255,255,0.7)" }}>
                            {isRevealed ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </Box>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyToClipboard(w.address)}
                          sx={{ mt: 1, color: "#2af598", borderColor: "rgba(42,245,152,0.4)", fontWeight: 700, "&:hover": { borderColor: "#2af598", bgcolor: "rgba(42,245,152,0.08)" } }}
                        >
                          Copy address
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Previously registered, now inactive */}
              {!loadingWallets && inactiveWallets.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 1.5 }} />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Previously registered (inactive)
                  </Typography>
                  <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {inactiveWallets.map((w) => {
                      const net = w.network || DEFAULT_NETWORK;
                      const isRevealed = revealed[w.id];
                      return (
                        <Box
                          key={w.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            border: "1px dashed rgba(255,255,255,0.18)",
                            bgcolor: "rgba(0,0,0,0.18)",
                            opacity: 0.85,
                          }}
                        >
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75, alignItems: "center" }}>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 700, mr: 0.5 }}>
                              {w.label || `${w.asset || DEFAULT_ASSET} Wallet`}
                            </Typography>
                            <Chip size="small" label={w.asset || DEFAULT_ASSET} variant="outlined" sx={{ height: 20, fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.25)" }} />
                            <Chip size="small" label={net} variant="outlined" sx={{ height: 20, fontSize: 10, color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)" }} />
                            <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)" }} />
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: "rgba(255,255,255,0.85)", wordBreak: "break-all", flex: 1 }}>
                              {isRevealed ? w.address : maskAddress(w.address, net)}
                            </Typography>
                            <IconButton size="small" onClick={() => toggleReveal(w.id)} sx={{ color: "rgba(255,255,255,0.6)" }}>
                              {isRevealed ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </Box>
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copyToClipboard(w.address)}
                            sx={{ mt: 1, color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)", fontWeight: 700, "&:hover": { borderColor: "rgba(255,255,255,0.4)", bgcolor: "rgba(255,255,255,0.04)" } }}
                          >
                            Copy address
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
            </Box>

            <Box sx={{ width: "100%", flex: { md: "1 1 0" }, minWidth: 0 }}>
            {/* ── Verify / confirm ── */}
            <Box sx={{ ...PANEL_SX, mb: 2 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Confirm the address the client sent
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant={showCamera ? "contained" : "outlined"}
                  startIcon={<QrCodeScannerIcon />}
                  onClick={() => setShowCamera((p) => !p)}
                  sx={{ flex: "1 1 140px", fontWeight: 700, color: showCamera ? "#000" : "#2af598", borderColor: "rgba(42,245,152,0.4)", bgcolor: showCamera ? "#2af598" : "transparent", "&:hover": { bgcolor: showCamera ? "#3adecc" : "rgba(42,245,152,0.08)" } }}
                >
                  {showCamera ? "Close scanner" : "Scan QR"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={decodingPhoto ? <CircularProgress size={16} /> : <ImageSearchIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={decodingPhoto}
                  sx={{ flex: "1 1 140px", fontWeight: 700, color: "#7eb8ff", borderColor: "rgba(126,184,255,0.4)", "&:hover": { borderColor: "#7eb8ff", bgcolor: "rgba(126,184,255,0.08)" } }}
                >
                  Upload photo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/*"
                  style={{ display: "none" }}
                  onChange={handlePhotoSelected}
                />
              </Box>
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: "rgba(255,255,255,0.5)", lineHeight: 1.4 }}>
                Scan QR opens the live camera. Upload photo lets you pick from Photo Library or Files (screenshots the client sent).
              </Typography>

              {showCamera && (
                <Box sx={{ mt: 2 }}>
                  <QrScanner
                    onDecode={handleCameraDecode}
                    onError={() => { toast.error("Could not access the camera. Use 'Upload photo' or paste the address."); setShowCamera(false); }}
                  />
                </Box>
              )}

              {proofPreview && (
                <Box sx={{ mt: 2, position: "relative", display: "inline-block" }}>
                  <Box
                    component="img"
                    src={proofPreview}
                    alt="Wallet proof"
                    sx={{ maxWidth: "100%", maxHeight: 220, borderRadius: 2, border: "1px solid rgba(255,255,255,0.15)" }}
                  />
                  <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "rgba(255,255,255,0.6)", mt: 0.5 }}>
                    <PhotoCameraIcon sx={{ fontSize: 14 }} /> Proof photo — saved with this confirmation
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                label="Wallet address"
                value={address}
                onChange={(e) => onAddressChange(e.target.value)}
                placeholder="Scan, upload, or paste the address (0x… / T… / bc1…)"
                sx={{ mt: 2 }}
                error={!!trimmedAddress && !addressValidation.valid}
                helperText={
                  trimmedAddress && !addressValidation.valid
                    ? addressValidation.message || "Address format not recognized"
                    : "Auto-detected network — adjust if needed"
                }
                InputLabelProps={{ sx: FIELD_LABEL_SX }}
                FormHelperTextProps={{ sx: { color: trimmedAddress && !addressValidation.valid ? undefined : "rgba(255,255,255,0.5)" } }}
                InputProps={{
                  sx: { ...FIELD_INPUT_SX, fontFamily: "monospace", fontSize: 14 },
                  endAdornment: address ? (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={() => copyToClipboard(trimmedAddress)}>
                          <ContentCopyIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.7)" }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                <TextField
                  select label="Asset" value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  size="small" sx={{ flex: 1 }}
                  InputLabelProps={{ sx: FIELD_LABEL_SX }}
                  SelectProps={{ sx: FIELD_INPUT_SX }}
                >
                  {CRYPTO_ASSETS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </TextField>
                <TextField
                  select label="Network" value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  size="small" sx={{ flex: 1.4 }}
                  InputLabelProps={{ sx: FIELD_LABEL_SX }}
                  SelectProps={{ sx: FIELD_INPUT_SX }}
                >
                  {CRYPTO_NETWORKS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
              </Box>

              {/* Result badge */}
              {matchConf && (
                <Box
                  sx={{
                    mt: 2, p: 1.5, borderRadius: 2,
                    display: "flex", alignItems: "flex-start", gap: 1.25,
                    bgcolor: matchConf.bg, border: `1px solid ${matchConf.border}`,
                  }}
                >
                  <matchConf.Icon sx={{ color: matchConf.color, fontSize: 28, flexShrink: 0, mt: 0.25 }} />
                  <Box>
                    <Typography variant="body1" sx={{ color: matchConf.color, fontWeight: 700, lineHeight: 1.3 }}>
                      {matchConf.label}
                    </Typography>
                    {matchedWallet ? (
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                        {matchedWallet.label || matchedWallet.id} · …{(matchedWallet.address || "").slice(-4)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block", lineHeight: 1.4 }}>
                        {matchConf.hint}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mt: 2 }}
                InputLabelProps={{ sx: FIELD_LABEL_SX }}
                InputProps={{ sx: FIELD_INPUT_SX }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={18} sx={{ color: "#000" }} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !trimmedAddress}
                sx={{
                  mt: 2, py: 1.25, fontWeight: 700,
                  background: "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
                  color: "#000",
                  "&:hover": { background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)" },
                  "&:disabled": { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)" },
                }}
              >
                {saving
                  ? "Saving…"
                  : matchStatus && matchStatus !== "match"
                  ? "Save new wallet for review"
                  : "Save confirmation"}
              </Button>
            </Box>

            {/* ── History ── */}
            <Box sx={{ ...PANEL_SX, mb: 2 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Recent confirmations
              </Typography>
              {loadingHistory ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Loading…</Typography>
                </Box>
              ) : history.length === 0 ? (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mt: 1.5 }}>
                  No confirmations recorded yet.
                </Typography>
              ) : (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                  {history.map((h) => {
                    const conf = MATCH_CONFIG[h.match_status] || MATCH_CONFIG.no_match;
                    const isRegistered = officialWallets.some(
                      (w) =>
                        normalizeAddressForCompare(w.address, w.network || DEFAULT_NETWORK) ===
                        normalizeAddressForCompare(h.address, h.network || DEFAULT_NETWORK)
                    );
                    return (
                      <Box key={h.id} sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid rgba(255,255,255,0.1)", bgcolor: "rgba(0,0,0,0.2)" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <conf.Icon sx={{ color: conf.color, fontSize: 18 }} />
                          <Typography variant="body2" sx={{ color: conf.color, fontWeight: 700, fontSize: 12 }}>
                            {conf.short}
                          </Typography>
                          <Chip size="small" label={`${h.asset || ""} ${h.network || ""}`.trim()} variant="outlined" sx={{ height: 18, fontSize: 9, color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)" }} />
                          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                            {h.photo_url && (
                              <Box
                                component="a"
                                href={h.photo_url}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ color: "#7eb8ff", fontSize: 11, textDecoration: "none" }}
                              >
                                View photo
                              </Box>
                            )}
                            {isRegistered ? (
                              <Chip
                                size="small"
                                label="Registered"
                                sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: "rgba(42,245,152,0.12)", color: "#2af598", border: "1px solid rgba(42,245,152,0.35)" }}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleImportClick(h)}
                                sx={{ minWidth: 0, py: 0.1, px: 1, fontSize: 11, fontWeight: 700, color: "#2af598", borderColor: "rgba(42,245,152,0.4)", "&:hover": { borderColor: "#2af598", bgcolor: "rgba(42,245,152,0.08)" } }}
                              >
                                Import
                              </Button>
                            )}
                          </Box>
                        </Box>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#fff", wordBreak: "break-all", mt: 0.5 }}>
                          {h.address}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)" }}>
                          {formatTs(h.confirmed_at)} · {h.confirmed_by || "admin"}
                          {h.notes ? ` · ${h.notes}` : ""}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
            </Box>
          </Box>
        )}

        {/* ── Team progress (indicator + table) ── */}
        <Box sx={{ ...PANEL_SX, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Team progress
            </Typography>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={loadProgress} disabled={loadingProgress} sx={{ color: "rgba(255,255,255,0.6)" }}>
                  {loadingProgress ? <CircularProgress size={16} /> : <QrCodeScannerIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
            {[
              { label: "Wallets recorded", value: progressStats.total, color: "#fff" },
              { label: "New for review", value: progressStats.newWallets, color: "#7eb8ff" },
              { label: "Matched", value: progressStats.matched, color: "#2af598" },
              { label: "Clients", value: progressStats.clientsTouched, color: "#fff" },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  flex: "1 1 90px",
                  minWidth: 80,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: "1px solid rgba(255,255,255,0.1)",
                  bgcolor: "rgba(0,0,0,0.2)",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: 24, fontWeight: 800, color: s.color, lineHeight: 1.1 }}>
                  {loadingProgress ? "—" : s.value}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Table — desktop only */}
          <Box sx={{ display: { xs: "none", md: "block" }, mt: 2 }}>
            {loadingProgress ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>Loading…</Typography>
              </Box>
            ) : allConfirmations.length === 0 ? (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                No wallets recorded yet.
              </Typography>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 360, borderRadius: 1.5, border: "1px solid rgba(255,255,255,0.1)" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {["Client", "Asset / Network", "Address", "Status", "By", "When", "Notes", "Proof"].map((h) => (
                          <TableCell
                            key={h}
                            sx={{ bgcolor: "#16201c", color: "rgba(255,255,255,0.6)", fontWeight: 700, fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.12)", whiteSpace: "nowrap" }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allConfirmations.slice(0, 50).map((c) => {
                        const conf = MATCH_CONFIG[c.match_status] || MATCH_CONFIG.no_match;
                        return (
                          <TableRow key={`${c.userId}-${c.id}`} hover>
                            <TableCell sx={{ color: "#fff", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {clientNameById[c.userId] || c.userId || "—"}
                            </TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                              {`${c.asset || ""} ${c.network || ""}`.trim() || "—"}
                            </TableCell>
                            <TableCell sx={{ color: "#fff", fontFamily: "monospace", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                              {maskAddress(c.address) || "—"}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              <Chip size="small" label={conf.short} sx={{ height: 22, fontSize: 11, fontWeight: 700, color: conf.color, bgcolor: conf.bg, border: `1px solid ${conf.border}` }} />
                            </TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {c.confirmed_by || "admin"}
                            </TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                              {formatTs(c.confirmed_at)}
                            </TableCell>
                            <TableCell
                              title={c.notes || ""}
                              sx={{ color: "rgba(255,255,255,0.75)", fontSize: 13, borderBottom: "1px solid rgba(255,255,255,0.06)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {c.notes?.trim() || "—"}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              {c.photo_url ? (
                                <Box component="a" href={c.photo_url} target="_blank" rel="noreferrer" sx={{ color: "#7eb8ff", fontSize: 13, textDecoration: "none" }}>
                                  View
                                </Box>
                              ) : (
                                <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: 13 }}>—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {allConfirmations.length > 50 && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 1 }}>
                    Showing the latest 50 of {allConfirmations.length}.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {selectedClient && (
        <AddWalletModal
          open={importOpen}
          onClose={() => { setImportOpen(false); setImportPrefill(null); }}
          onSuccess={reloadAfterImport}
          preselectedClient={{ doc_id: selectedClient.doc_id || selectedClient.id }}
          prefill={importPrefill}
        />
      )}
    </Box>
  );
};

export default WalletCheck;
